import { _isEmpty, _isNil, _isNotEmpty, _notNil, safelyDecodeURIComponent, uuid } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type {
  DisplayedGroupMetadata,
  MetadataFieldForOptionsValidation,
  MetadataFieldValueTypes,
  MetadataFieldWithValue,
} from '@/model/Metadata.model';
import { type TreatmentGroup } from '@/model/TreatmentGroup.model.ts';
import type { ClassificationType, Treatment } from '@/model/Treatment.model.ts';
import { validationCache } from '@/utils/ValidationRequestCache';

export const tempEntityArrayToMap = <T extends { temp_id?: string | undefined }>(entities: Array<T>): Map<ID, T> =>
  new Map(
    (entities ?? []).map((entity) => {
      const tempId = entity?.temp_id ?? uuid();
      return [tempId, { ...entity, temp_id: tempId }];
    })
  );

export type InvalidMetadataFields = { type: 'invalid-metadata'; fields: Array<ID> };
export type InvalidMetadataOptions = { type: 'invalid-options'; fields: Array<ID> };
export type GroupValidationIssue = InvalidMetadataFields | InvalidMetadataOptions;
export type TreatmentValidationIssue = InvalidMetadataOptions;

export const validateGroups = async (
  groups: Array<TreatmentGroup>,
  metadataFields: Array<ID>,
  savedGroupData?: Array<DisplayedGroupMetadata>
): Promise<Map<string, GroupValidationIssue>> => {
  const issues = new Map<string, GroupValidationIssue>();

  for (const group of groups) {
    const invalidMetadataIssue = validateGroupMetadata(group, metadataFields);
    if (_notNil(invalidMetadataIssue)) {
      issues.set(group?.temp_id ?? '', invalidMetadataIssue);
      continue;
    }

    const invalidOptionsIssue = await validateGroupMetadataOptions(group, savedGroupData);
    if (_notNil(invalidOptionsIssue)) {
      issues.set(group?.temp_id ?? '', invalidOptionsIssue);
    }
  }

  return issues;
};

const validateGroupMetadata = (group: TreatmentGroup, metadataFields: Array<ID>): InvalidMetadataFields | undefined => {
  const groupGlossaryIds = new Set(group?.metadata?.map(({ glossary_id }) => glossary_id) ?? []);
  const invalidMetadataFields = (metadataFields ?? []).reduce<Array<ID>>((acc, metadataField: ID) => {
    if (!groupGlossaryIds.has(metadataField)) {
      acc.push(metadataField);
    }
    return acc;
  }, []);
  if (invalidMetadataFields.length > 0) {
    return { type: 'invalid-metadata', fields: invalidMetadataFields };
  }
};

const SELECT_FIELD_TYPE_REGEX = /^(?<isLookup>lookup_)?(?:multi_)?select$/;

const isSelectFieldType = (fieldType: string): boolean => SELECT_FIELD_TYPE_REGEX.test(fieldType);

const isLookupSelectFieldType = (fieldType: string): boolean => {
  const match = SELECT_FIELD_TYPE_REGEX.exec(fieldType);
  return _notNil(match?.groups?.isLookup);
};

const validateGroupMetadataOptions = async (
  group: TreatmentGroup,
  savedGroupData?: Array<DisplayedGroupMetadata>
): Promise<InvalidMetadataOptions | undefined> => {
  if (_isNil(savedGroupData) || _isEmpty(savedGroupData)) {
    return undefined;
  }
  const savedGroupDataMap = new Map(savedGroupData.map((field) => [field.schema.id, field.schema]));
  const invalidOptionFields: Array<ID> = [];

  await Promise.all(
    (group?.metadata ?? []).map(async (metadataField) => {
      if (!isSelectFieldType(metadataField.field_type)) {
        return;
      }
      const savedField = savedGroupDataMap.get(metadataField.glossary_id);
      const hasInvalidOptions = await validateFieldOptions(metadataField, savedField);
      if (hasInvalidOptions) {
        invalidOptionFields.push(metadataField.glossary_id);
      }
    })
  );

  if (_isNotEmpty(invalidOptionFields)) {
    return { type: 'invalid-options', fields: invalidOptionFields };
  }
};

const validateFieldOptions = async (
  metadataField: MetadataFieldWithValue,
  savedField: DisplayedGroupMetadata['schema'] | undefined
): Promise<boolean> => {
  if (_isNil(metadataField?.value)) {
    return false;
  }

  if (isLookupSelectFieldType(metadataField.field_type)) {
    return validateLookupFieldOptions(metadataField);
  }

  if (_isNil(savedField?.options)) {
    return false;
  }

  return checkForInvalidOptions(metadataField.value, savedField.options);
};

const validateLookupFieldOptions = async (metadataField: MetadataFieldWithValue): Promise<boolean> => {
  const glossaryId = metadataField.glossary_id;
  if (typeof glossaryId !== 'number') {
    return false;
  }

  const values = getValuesAsSet(metadataField.value, true);
  if (values.size === 0) {
    return false;
  }

  const result = await validationCache.validate(glossaryId, values);
  return !result.valid;
};

const getValuesAsSet = (value: MetadataFieldValueTypes | undefined, decode: boolean = false): Set<string> => {
  if (_isNil(value)) {
    return new Set();
  }
  if (Array.isArray(value)) {
    return new Set(value.map((v) => (decode ? safelyDecodeURIComponent(String(v)) : String(v))));
  }
  const stringValue = String(value);
  return new Set([decode ? safelyDecodeURIComponent(stringValue) : stringValue]);
};

const tryParseJsonArray = (value: string): Array<unknown> | null => {
  if (!value.startsWith('[') || !value.endsWith(']')) {
    return null;
  }
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const checkForInvalidOptions = (value: MetadataFieldValueTypes | undefined, validOptions: Array<string>): boolean => {
  if (_isNil(value)) {
    return false;
  }

  // Decode valid options as they may be URL-encoded
  const decodedOptions = new Set(validOptions.map((opt) => safelyDecodeURIComponent(opt)));

  // Handle JSON string arrays (multi_select values are often stored as JSON strings)
  let parsedValue: MetadataFieldValueTypes | Array<unknown> = value;
  if (typeof value === 'string') {
    const jsonArray = tryParseJsonArray(value);
    if (jsonArray !== null) {
      parsedValue = jsonArray;
    }
  }

  if (Array.isArray(parsedValue)) {
    // Decode URL-encoded values before comparison
    return parsedValue.some((v) => !decodedOptions.has(safelyDecodeURIComponent(String(v))));
  }

  // Decode URL-encoded value before comparison
  return !decodedOptions.has(safelyDecodeURIComponent(String(parsedValue)));
};

export const filterTreatmentsByClassification = (
  treatments: Array<Treatment>,
  classificationType: ClassificationType
) => {
  return treatments.filter((treatment) =>
    classificationType === 'disease_induction'
      ? treatment.treatment_classification === 'disease_induction'
      : treatment.treatment_classification !== 'disease_induction'
  );
};

export const validateTreatments = (
  treatments: Array<Treatment>,
  savedTreatmentData?: Array<MetadataFieldForOptionsValidation>
): Map<string, TreatmentValidationIssue> => {
  const issues = new Map<string, TreatmentValidationIssue>();

  if (_isNil(savedTreatmentData) || _isEmpty(savedTreatmentData)) {
    return issues;
  }

  const savedDataMap = new Map(savedTreatmentData.map((field) => [field.id, field]));

  for (const treatment of treatments) {
    const selectFields = (treatment?.metadata ?? []).filter(
      (field) => isSelectFieldType(field.field_type) && !isLookupSelectFieldType(field.field_type)
    );

    const invalidFields: Array<ID> = [];

    for (const metadataField of selectFields) {
      const savedField = savedDataMap.get(metadataField.glossary_id);

      if (_notNil(savedField?.options) && _notNil(metadataField.value)) {
        const hasInvalidOptions = checkForInvalidOptions(metadataField.value, savedField.options);
        if (hasInvalidOptions) {
          invalidFields.push(metadataField.glossary_id);
        }
      }
    }

    if (_isNotEmpty(invalidFields)) {
      issues.set(treatment?.temp_id ?? '', { type: 'invalid-options', fields: invalidFields });
    }
  }

  return issues;
};
